import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import isEqual from 'lodash/isEqual'
import { Ban, ChevronDown, ChevronRight, Pen, Plus, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 } from 'uuid'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import { CodeEditor } from 'v2/shared/components/code-editor'
import type { SecretVariable } from 'v2/shared/services/external-apps-service'
import { logger } from 'v2/utils/logger'
import { SecretVariablesPopover } from './secret-variables.popover'

interface KeyValuePair {
  key: string
  value: string
  id: string
  children?: KeyValuePair[]
  isExpanded?: boolean
}
interface NestedRecord {
  [key: string]: string | NestedRecord
}

interface JsonKeyValueEditorProps {
  initialData?: NestedRecord
  onChange: (data: NestedRecord) => void
  apiRequestVars?: boolean
  nodeVars?: string[]
  /** The key pickers' list, when keys may use fewer variables than values; defaults to nodeVars */
  keyVars?: string[]
  isJsonModeEnabled?: boolean
  showJsonModeToggle?: boolean
  showSecretVariables?: boolean
}

interface PairRowNestingControlsProps {
  hasParent: boolean
  isObject: boolean
  isExpanded?: boolean
  apiRequestVars: boolean
  uniqueId: string
  onToggleExpanded: () => void
}

const PairRowNestingControls: React.FC<PairRowNestingControlsProps> = ({
  hasParent,
  isObject,
  isExpanded,
  apiRequestVars,
  uniqueId,
  onToggleExpanded,
}) => {
  return (
    <>
      {hasParent && (
        <div className="flex items-center h-10">
          <div className="w-3 h-[2px] bg-st2 -ml-3" />
        </div>
      )}

      {apiRequestVars && isObject ? (
        <Button
          onClick={onToggleExpanded}
          variant="ghost"
          type="button"
          className="p-0 h-10 w-3 hover:bg-transparent flex items-center justify-center shrink-0"
          data-testid={`button-toggle-${uniqueId}`}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-s-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-s-400" />
          )}
        </Button>
      ) : (
        apiRequestVars && <div className="w-3 shrink-0" />
      )}
    </>
  )
}

const PAYLOAD_KEY_PATTERN = /^[a-zA-Z0-9 _.-]*$/

interface PairRowInputsProps {
  pair: KeyValuePair
  index: number
  uniqueId: string
  apiRequestVars: boolean
  nodeVars?: string[]
  keyVars?: string[]
  showSecretVariables?: boolean
  onUpdatePair: (id: string, field: 'key' | 'value', newValue: string) => void
  onRemovePair: (id: string) => void
  onSecretVariableClick: (variable: SecretVariable, inputId: string) => void
}

const PairRowInputs: React.FC<PairRowInputsProps> = ({
  pair,
  index,
  uniqueId,
  apiRequestVars,
  nodeVars,
  keyVars,
  showSecretVariables,
  onUpdatePair,
  onRemovePair,
  onSecretVariableClick,
}) => {
  const keyError = apiRequestVars && pair.key && !PAYLOAD_KEY_PATTERN.test(pair.key)
  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="flex-1 min-w-0 flex flex-col">
        <Input
          placeholder="Key"
          value={pair.key}
          onChange={(e) => onUpdatePair(pair.id, 'key', e.target.value)}
          id={`json-key-${uniqueId}`}
          className="w-full"
          inputClassName={`font-jetbrains text-xs-400 ${apiRequestVars ? 'pr-36' : 'pr-14'}`}
          error={
            keyError ? 'Only letters, digits, dots, underscores, and hyphens allowed' : undefined
          }
          endIcon={
            apiRequestVars ? (
              <div className="flex items-center gap-1">
                <Select
                  value={pair.value}
                  variant="ghost"
                  // Cap width so a long/invalid value (shown in red) truncates
                  // instead of overflowing left over the key label. SER-4772.
                  className="p-0 w-fit max-w-[140px]"
                  popoverContentClassName="w-[120px]"
                  placeholder="Select type"
                  searchable={false}
                  onChange={(value) => onUpdatePair(pair.id, 'value', value as string)}
                  options={[
                    { value: 'string', label: 'String' },
                    { value: 'number', label: 'Number' },
                    { value: 'boolean', label: 'Boolean' },
                    { value: 'list', label: 'List' },
                    { value: 'json', label: 'JSON String' },
                    { value: 'object', label: 'Nested Object' },
                  ]}
                />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <NodevarList
                  nodevars={keyVars}
                  inputElementSelector={`#json-key-${uniqueId}`}
                  sideOffset={195}
                />
                {showSecretVariables && (
                  <SecretVariablesPopover
                    sideOffset={213}
                    onVariableClick={(variable) =>
                      onSecretVariableClick(variable, `json-key-${uniqueId}`)
                    }
                  />
                )}
              </div>
            )
          }
        />
      </div>
      {!apiRequestVars && (
        <Input
          placeholder="Value"
          value={pair.value}
          onChange={(e) => onUpdatePair(pair.id, 'value', e.target.value)}
          className="flex-1 min-w-0"
          inputClassName="pr-14 font-jetbrains text-xs-400"
          id={`json-value-${uniqueId}`}
          endIcon={
            <div className="flex items-center gap-1">
              <NodevarList
                nodevars={nodeVars}
                inputElementSelector={`#json-value-${uniqueId}`}
                sideOffset={416}
              />
              {showSecretVariables && (
                <SecretVariablesPopover
                  sideOffset={435}
                  onVariableClick={(variable) =>
                    onSecretVariableClick(variable, `json-value-${uniqueId}`)
                  }
                />
              )}
            </div>
          }
        />
      )}
      {index > 0 || pair.key || pair.value ? (
        <Button
          onClick={() => onRemovePair(pair.id)}
          variant="ghost"
          type="button"
          className="text-s-400 hover:text-s-500 hover:bg-transparent p-0 h-10 w-10 shrink-0"
          data-testid={`button-remove-pair-${uniqueId}`}
        >
          <X className="h-5 w-5" />
        </Button>
      ) : (
        <div className="text-secondary cursor-not-allowed h-10 w-10 flex items-center justify-center shrink-0">
          <Ban className="h-5 w-5" />
        </div>
      )}
    </div>
  )
}

interface PairRowNestedChildrenProps {
  pair: KeyValuePair
  isObject: boolean
  hasChildren: boolean
  uniqueId: string
  apiRequestVars: boolean
  onAddNestedField: (parentId: string) => void
  renderPairRow: (
    pair: KeyValuePair,
    index: number,
    parentPath: string,
    parentChain: string[],
  ) => React.ReactNode
  parentChain: string[]
}

const PairRowNestedChildren: React.FC<PairRowNestedChildrenProps> = ({
  pair,
  isObject,
  hasChildren,
  uniqueId,
  apiRequestVars,
  onAddNestedField,
  renderPairRow,
  parentChain,
}) => {
  if (!apiRequestVars || !isObject || !pair.key || !pair.isExpanded) {
    return null
  }

  return (
    <div className="relative pt-1 space-y-1">
      <div className="absolute left-3 top-0 bottom-0 w-[2px] bg-st2 -mt-1" aria-hidden />
      {hasChildren &&
        pair.children &&
        pair.children.map((child, childIndex) =>
          renderPairRow(child, childIndex, uniqueId, [...parentChain, pair.id]),
        )}

      <div className="relative pl-6">
        <div className="absolute left-3 top-0 bottom-0 w-[2px] bg-st2 -translate-y-4" aria-hidden />
        <div
          className="absolute left-3 top-1/2 h-[2px] w-3 bg-st2 -translate-y-1/2 transform"
          aria-hidden
        />
        <Button
          onClick={() => onAddNestedField(pair.id)}
          variant="ghost"
          size="sm"
          type="button"
          className="font-inter text-xs-500 text-primary hover:text-primary hover:bg-primary/5 h-8"
          data-testid={`button-add-nested-${uniqueId}`}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add nested field
        </Button>
      </div>
    </div>
  )
}

// Converts nested JSON object to flat key-value pair array
const convertToKeyValuePairs = (data: NestedRecord): KeyValuePair[] => {
  return Object.entries(data).map(([key, value]) => {
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0
    ) {
      // It's a nested object
      return {
        key,
        value: 'object',
        id: v4(),
        children: convertToKeyValuePairs(value as Record<string, string>),
        isExpanded: true,
      }
    }
    // It's a primitive or array/json
    return {
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
      id: v4(),
      children: [],
      isExpanded: true,
    }
  })
}

// Converts flat key-value pair array back to nested JSON object

const convertToNestedData = (pairs: KeyValuePair[], apiRequestVars: boolean): NestedRecord => {
  return pairs.reduce((acc, pair) => {
    if (pair.key && pair.value) {
      if (pair.value === 'object' && pair.children && pair.children.length > 0) {
        // Recursively convert children to nested object
        acc[pair.key] = convertToNestedData(pair.children, apiRequestVars)
      } else if (pair.value !== 'object') {
        // In apiRequestVars mode, store type strings as-is
        // In regular mode, keep as string (parsing handled elsewhere if needed)
        acc[pair.key] = pair.value
      }
    }
    return acc
  }, {} as NestedRecord)
}

export const JsonKeyValueEditor: React.FC<JsonKeyValueEditorProps> = ({
  initialData = {},
  onChange,
  apiRequestVars = false,
  nodeVars,
  keyVars,
  isJsonModeEnabled = false,
  showJsonModeToggle = true,
  showSecretVariables = false,
}) => {
  const createEmptyPair = useCallback(
    (): KeyValuePair => ({
      key: '',
      value: apiRequestVars ? 'string' : '',
      id: v4(),
      children: [],
      isExpanded: true,
    }),
    [apiRequestVars],
  )

  const initializePairs = useCallback(() => {
    const pairs = convertToKeyValuePairs(initialData)
    // Always ensure at least one empty pair for new entries
    pairs.push(createEmptyPair())
    return pairs
  }, [initialData, createEmptyPair])

  const [pairs, setPairs] = useState<KeyValuePair[]>(initializePairs)
  const internalDataRef = useRef(initialData)
  const isInternalChangeRef = useRef(false)

  useEffect(() => {
    if (isInternalChangeRef.current) return
    if (!isEqual(initialData, internalDataRef.current)) {
      internalDataRef.current = initialData
      setPairs(initializePairs)
    }
  }, [initializePairs, initialData])
  const [isJsonMode, setIsJsonMode] = useState(isJsonModeEnabled)

  const updateData = useCallback(
    (newPairs: KeyValuePair[]) => {
      const newData = convertToNestedData(newPairs, apiRequestVars)
      isInternalChangeRef.current = true
      onChange(newData)
      setTimeout(() => {
        isInternalChangeRef.current = false
      }, 0)
    },
    [onChange, apiRequestVars],
  )

  const updatePairRecursive = useCallback(
    (
      pairs: KeyValuePair[],
      id: string,
      field: 'key' | 'value',
      newValue: string,
    ): KeyValuePair[] => {
      return pairs.map((pair) => {
        if (pair.id === id) {
          const updatedPair = { ...pair, [field]: newValue }
          // If changing to 'object' type, initialize children if empty
          if (field === 'value' && newValue === 'object' && !updatedPair.children?.length) {
            updatedPair.children = [createEmptyPair()]
          }
          return updatedPair
        }
        // Recursively update children
        if (pair.children && pair.children.length > 0) {
          return {
            ...pair,
            children: updatePairRecursive(pair.children, id, field, newValue),
          }
        }
        return pair
      })
    },
    [createEmptyPair],
  )

  const updatePair = useCallback(
    (id: string, field: 'key' | 'value', newValue: string) => {
      const newPairs = updatePairRecursive(pairs, id, field, newValue)

      // Add a new empty pair only if the last pair has content and there isn't already an empty one
      const lastPair = newPairs[newPairs.length - 1]
      const hasEmptyPair = newPairs.some(
        (pair) => !pair.key && (!pair.value || pair.value === (apiRequestVars ? 'string' : '')),
      )

      if (
        !hasEmptyPair &&
        (lastPair.key || (lastPair.value && lastPair.value !== (apiRequestVars ? 'string' : '')))
      ) {
        newPairs.push(createEmptyPair())
      }

      setPairs(newPairs)
      updateData(newPairs)
    },
    [pairs, apiRequestVars, createEmptyPair, updateData, updatePairRecursive],
  )

  const removePairRecursive = useCallback((pairs: KeyValuePair[], id: string): KeyValuePair[] => {
    return pairs
      .filter((pair) => pair.id !== id)
      .map((pair) => {
        if (pair.children && pair.children.length > 0) {
          return {
            ...pair,
            children: removePairRecursive(pair.children, id),
          }
        }
        return pair
      })
  }, [])

  const removePair = useCallback(
    (id: string) => {
      const newPairs = removePairRecursive(pairs, id)

      // Ensure we always have at least one empty pair at root level
      const hasEmptyPair = newPairs.some(
        (pair) => !pair.key && (!pair.value || pair.value === (apiRequestVars ? 'string' : '')),
      )
      if (!hasEmptyPair) {
        newPairs.push(createEmptyPair())
      }

      setPairs(newPairs)
      updateData(newPairs)
    },
    [pairs, apiRequestVars, createEmptyPair, updateData, removePairRecursive],
  )

  const addNestedField = useCallback(
    (parentId: string) => {
      const addNestedRecursive = (pairs: KeyValuePair[]): KeyValuePair[] => {
        return pairs.map((pair) => {
          if (pair.id === parentId) {
            const newChild = createEmptyPair()
            return {
              ...pair,
              children: [...(pair.children || []), newChild],
            }
          }
          if (pair.children && pair.children.length > 0) {
            return {
              ...pair,
              children: addNestedRecursive(pair.children),
            }
          }
          return pair
        })
      }

      const newPairs = addNestedRecursive(pairs)
      setPairs(newPairs)
      updateData(newPairs)
    },
    [pairs, createEmptyPair, updateData],
  )

  const toggleExpanded = useCallback(
    (id: string) => {
      const toggleRecursive = (pairs: KeyValuePair[]): KeyValuePair[] => {
        return pairs.map((pair) => {
          if (pair.id === id) {
            return { ...pair, isExpanded: !pair.isExpanded }
          }
          if (pair.children && pair.children.length > 0) {
            return {
              ...pair,
              children: toggleRecursive(pair.children),
            }
          }
          return pair
        })
      }

      setPairs(toggleRecursive(pairs))
    },
    [pairs],
  )

  const handleJsonChange = useCallback(
    (newValue: string) => {
      try {
        const parsedData = JSON.parse(newValue)
        const newPairs = convertToKeyValuePairs(parsedData)
        // Add one empty pair for new entries
        newPairs.push(createEmptyPair())
        setPairs(newPairs)
        isInternalChangeRef.current = true
        onChange(parsedData)
        setTimeout(() => {
          isInternalChangeRef.current = false
        }, 0)
      } catch (error) {
        logger.warn('Error parsing JSON', { error })
      }
    },
    [createEmptyPair, onChange],
  )

  const handleSecretVariableClick = useCallback(
    (variable: SecretVariable, inputId: string) => {
      const input = document.getElementById(inputId) as HTMLInputElement
      if (!input) return

      const currentValue = input.value
      const cursorPosition = input.selectionStart || 0
      const newValue = `${currentValue.slice(
        0,
        cursorPosition,
      )}{{Secrets.${variable.name}}}${currentValue.slice(cursorPosition)}`

      // Find the pair and update it
      const pairIndex = parseInt(inputId.replace(/^json-(key|value)-/, ''), 10)
      const pair = pairs[pairIndex]

      if (pair) {
        const field = inputId.includes('json-key-') ? 'key' : 'value'
        updatePair(pair.id, field, newValue)
      }
    },
    [pairs, updatePair],
  )

  const currentJsonValue = useMemo(() => {
    return JSON.stringify(convertToNestedData(pairs, apiRequestVars), null, 2)
  }, [pairs, apiRequestVars])

  const renderPairRow = (
    pair: KeyValuePair,
    index: number,
    parentPath = '',
    parentChain: string[] = [],
  ): React.ReactNode => {
    const hasChildren = Boolean(pair.children && pair.children.length > 0)
    const isObject = pair.value === 'object'
    const uniqueId = `${parentPath}-${index}`
    const hasParent = parentChain.length > 0

    return (
      <div key={pair.id} className={hasParent ? 'relative pl-6' : 'relative'}>
        {hasParent && <div className="absolute left-3 top-0 bottom-0 w-[2px] bg-st2" aria-hidden />}

        <div className="flex items-start gap-2">
          <PairRowNestingControls
            hasParent={hasParent}
            isObject={isObject}
            isExpanded={pair.isExpanded}
            apiRequestVars={apiRequestVars}
            uniqueId={uniqueId}
            onToggleExpanded={() => toggleExpanded(pair.id)}
          />

          <PairRowInputs
            pair={pair}
            index={index}
            uniqueId={uniqueId}
            apiRequestVars={apiRequestVars}
            nodeVars={nodeVars}
            keyVars={keyVars ?? nodeVars}
            showSecretVariables={showSecretVariables}
            onUpdatePair={updatePair}
            onRemovePair={removePair}
            onSecretVariableClick={handleSecretVariableClick}
          />
        </div>

        <PairRowNestedChildren
          pair={pair}
          isObject={isObject}
          hasChildren={hasChildren}
          uniqueId={uniqueId}
          apiRequestVars={apiRequestVars}
          onAddNestedField={addNestedField}
          renderPairRow={renderPairRow}
          parentChain={[...parentChain, pair.id]}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {isJsonMode ? (
        <div className="border rounded-md p-2 bg-bg2">
          <CodeEditor
            isKeyValueEditor
            allowNestedValues={apiRequestVars}
            value={currentJsonValue}
            onChange={handleJsonChange}
          />
        </div>
      ) : (
        <div className="space-y-1">
          {pairs.map((pair, index) => renderPairRow(pair, index, '', []))}
        </div>
      )}
      {showJsonModeToggle && (
        <Button
          variant="ghost"
          onClick={() => setIsJsonMode(!isJsonMode)}
          type="button"
          data-testid="button-toggle-json-mode"
        >
          <Pen className="h-4 w-4 mr-2" /> {isJsonMode ? 'Key-Value' : 'JSON'}
        </Button>
      )}
    </div>
  )
}
